import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import type { MetricKey } from "contracts/report/catalog";
import { firstPathSegment } from "../../boundaries/url-path.parser";
import {
  ISSUE_PRESENTATION_SIGNIFICANCE,
  type IssuePresentationSignificance,
} from "../../enums/issues";
import { isOpen, SEVERITY_ORDER } from "../website/issueModel";
import { uniqueCurrentPagesForIssue } from "./issuePageResolution";

export type IssuePresentationPriority = {
  openRank: number;
  severityRank: number;
  withinSeverityScore: number;
  significance: IssuePresentationSignificance;
  currentPageCount: number;
  estateAreaCount: number;
  familyIssueCount: number;
};

const SIGNIFICANCE_POINTS: Record<IssuePresentationSignificance, number> = {
  [ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL]: 260,
  [ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY]: 130,
  [ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE]: 0,
};

const SUBCRITERION_SIGNIFICANCE: Readonly<
  Record<string, IssuePresentationSignificance>
> = {
  "human_first_content_quality.content_value_substance":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "human_first_content_quality.clarity_language":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "human_first_content_quality.access_responsiveness":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "content_design_orientation.heading_hierarchy":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "content_design_orientation.metadata_titles":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "content_design_orientation.structured_data":
    ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  "content_design_orientation.internal_linking_depth":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "content_design_orientation.machine_readability":
    ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  "content_design_orientation.semantic_structure_markup":
    ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  "content_design_orientation.content_order_chunking":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "content_design_orientation.url_design":
    ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  "ai_agent_readiness.crawlable_indexable":
    ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  "ai_agent_readiness.server_rendered_extractable_content":
    ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  "ai_agent_readiness.structured_data_meaning":
    ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  "ai_agent_readiness.content_parity_no_cloaking":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "ai_agent_readiness.self_contained_citable_answers":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "ai_agent_readiness.multimodal_clarity":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "ai_agent_readiness.freshness_stable_urls":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "journey_usability_action.intent_journey_stage_fit":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "journey_usability_action.audience_fit":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "journey_usability_action.scannability_formatting":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "journey_usability_action.navigation_link_quality":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "journey_usability_action.action_path_friction_proof":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "brand_trust_consistency.brand_foundation_identity":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "brand_trust_consistency.imagery_art_direction":
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  "brand_trust_consistency.tone_voice_messaging_audience":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  "brand_trust_consistency.editorial_style_language":
    ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
};

const METRIC_SIGNIFICANCE = {
  cwv: ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  human_first_content_quality:
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  content_design_orientation:
    ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  ai_agent_readiness: ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  journey_usability_action: ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  brand_trust_consistency: ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  voice_trust: ISSUE_PRESENTATION_SIGNIFICANCE.DECISION_CRITICAL,
  inclusion: ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  source_control: ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
  prominence: ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  accuracy: ISSUE_PRESENTATION_SIGNIFICANCE.STRUCTURAL_QUALITY,
  competitor_leakage: ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE,
} as const satisfies Record<MetricKey, IssuePresentationSignificance>;

/**
 * Ranks presentation priority only. It does not alter issue severity or
 * Content Fitness scoring. Severity remains the outer ordering band; within a
 * band, supported content-decision significance and current estate scope lead.
 */
export function rankIssuesForClientPriority(
  data: OverviewResponse,
  issues: Issue[],
): Issue[] {
  const context = priorityContext(data, issues);
  return [...issues].sort((left, right) => {
    const comparison = compareIssuePresentationPriority(
      presentationPriorityFromContext(data, left, context),
      presentationPriorityFromContext(data, right, context),
    );
    return comparison || left.id.localeCompare(right.id);
  });
}

export function presentationPriorityForIssue(
  data: OverviewResponse,
  issue: Issue,
  issues: Issue[] = [issue],
): IssuePresentationPriority {
  return presentationPriorityFromContext(
    data,
    issue,
    priorityContext(data, issues),
  );
}

export function compareIssuePresentationPriority(
  left: IssuePresentationPriority,
  right: IssuePresentationPriority,
): number {
  return (
    left.openRank - right.openRank ||
    left.severityRank - right.severityRank ||
    right.withinSeverityScore - left.withinSeverityScore ||
    right.currentPageCount - left.currentPageCount ||
    right.estateAreaCount - left.estateAreaCount ||
    right.familyIssueCount - left.familyIssueCount
  );
}

export function canonicalIssueProblemKey(issue: Issue): string | null {
  return issue.problemKey;
}

export function clientPriorityFamilyKey(issue: Issue): string {
  const problemKey = canonicalIssueProblemKey(issue);
  if (!problemKey) return `issue:${issue.id}`;
  const subject = issue.primarySubcriterionKey ?? issue.metricKey;
  return `canonical:${subject}:${problemKey}`;
}

function presentationPriorityFromContext(
  data: OverviewResponse,
  issue: Issue,
  context: PriorityContext,
): IssuePresentationPriority {
  const family = context.get(contextKey(issue));
  const pages = family?.pages ?? uniqueCurrentPagesForIssue(data, issue);
  const areas = new Set(pages.map((page) => estateArea(page.path)));
  const significance = presentationSignificance(issue);
  const pagePoints = Math.min(pages.length, 10) * 18;
  const areaPoints = Math.min(areas.size, 4) * 12;
  const familyIssueCount = family?.issueIds.size ?? 1;
  const recurrencePoints = Math.min(Math.max(familyIssueCount - 1, 0), 4) * 8;
  return {
    openRank: isOpen(issue) ? 0 : 1,
    severityRank: SEVERITY_ORDER[issue.severity],
    withinSeverityScore:
      SIGNIFICANCE_POINTS[significance] +
      pagePoints +
      areaPoints +
      recurrencePoints,
    significance,
    currentPageCount: pages.length,
    estateAreaCount: areas.size,
    familyIssueCount,
  };
}

function presentationSignificance(issue: Issue): IssuePresentationSignificance {
  if (issue.originResourceKind !== null) {
    return ISSUE_PRESENTATION_SIGNIFICANCE.SUPPORTING_HYGIENE;
  }
  if (issue.primarySubcriterionKey) {
    const significance =
      SUBCRITERION_SIGNIFICANCE[issue.primarySubcriterionKey];
    if (significance) return significance;
  }
  return METRIC_SIGNIFICANCE[issue.metricKey];
}

type PriorityFamilyContext = {
  pages: OverviewResponse["pages"];
  issueIds: Set<string>;
};
type PriorityContext = Map<string, PriorityFamilyContext>;

function priorityContext(
  data: OverviewResponse,
  issues: Issue[],
): PriorityContext {
  const grouped = new Map<
    string,
    {
      pages: Map<string, OverviewResponse["pages"][number]>;
      issueIds: Set<string>;
    }
  >();
  for (const issue of issues) {
    const key = contextKey(issue);
    const family = grouped.get(key) ?? {
      pages: new Map(),
      issueIds: new Set<string>(),
    };
    for (const page of uniqueCurrentPagesForIssue(data, issue)) {
      family.pages.set(page.id, page);
    }
    family.issueIds.add(issue.id);
    grouped.set(key, family);
  }
  return new Map(
    [...grouped.entries()].map(([key, family]) => [
      key,
      { pages: [...family.pages.values()], issueIds: family.issueIds },
    ]),
  );
}

function contextKey(issue: Issue): string {
  return `${isOpen(issue) ? "open" : "closed"}:${clientPriorityFamilyKey(issue)}`;
}

function estateArea(path: string): string {
  return firstPathSegment(path, "home");
}
